import { ViewportService } from '@services/geo/viewport.service';
import { FilterComponent } from '@shared/components/filter/filter.component';

/**
 * Base class for handling reusable pagination, sorting, and filtering.
 */
export abstract class TableBase {
	// Current page
	page = 1;

	// Default values
	protected readonly DEFAULT_LIMIT = 10;
	protected readonly DEFAULT_LIMIT_MOBILE = 7;

	// Pagination values and total records
	limit = this.DEFAULT_LIMIT;
	totalRecords = 0;

	// Main field (should be overridden if needed)
	mainField = 'id';

	// Sorting
	sortField = this.mainField;
	sortOrder = 1;

	// Viewport detection
	isMobile = false;

	// Loading state for skeleton
	isLoading = false;

	// ====== Filter Properties ======
	filterFields: any[] = [];
	activeFilterChips: { key: string; label: string; value: any }[] = [];
	currentFilters: any = {};

	/**
	 * Filter keys that are applied but don't show chips.
	 * Use this for default/programmatic filters that users shouldn't see or remove.
	 */
	hiddenFilterKeys: string[] = [];

	constructor(protected viewportService: ViewportService) {
		this.isMobile = this.viewportService.isMobileView();
		this.limit = this.isMobile ? this.DEFAULT_LIMIT_MOBILE : this.DEFAULT_LIMIT;
	}

	/**
	 * Must be implemented by child component to reload data.
	 */
	abstract loadData(): void;

	/**
	 * Handles pagination changes emitted by the table.
	 */
	onPageChange(event: { first: number; rows: number }): void {
		if (!event || event.first === undefined || event.rows === undefined) {
			return;
		}

		const newPage = Math.floor(event.first / event.rows) + 1;

		if (this.page !== newPage || this.limit !== event.rows) {
			this.page = newPage;
			this.limit = event.rows;
			this.loadData();
		}
	}

	/**
	 * Handles table sorting events.
	 */
	onSort(event?: { field: string }): void {
		const field = event?.field || this.sortField;

		if (this.sortField === field) {
			this.sortOrder = this.sortOrder === 1 ? -1 : 1;
		} else {
			this.sortField = field;
			this.sortOrder = 1;
		}

		this.loadData();
	}

	// ====== Filter Methods ======

	/**
	 * Updates active filter chips based on currentFilters and filterFields.
	 * Filters in hiddenFilterKeys are applied but don't show chips.
	 * Can be overridden in child components for custom chip formatting.
	 */
	protected updateFilterChips(): void {
		this.activeFilterChips = [];

		for (const key in this.currentFilters) {
			// Skip hidden filters - they apply but don't show chips
			if (this.hiddenFilterKeys.includes(key)) {
				continue;
			}

			const value = this.currentFilters[key];
			if (value !== null && value !== undefined && value !== '') {
				const field = this.filterFields.find((f) => f.key === key);
				if (field) {
					let displayValue = value;

					if (field.type === 'select' && field.options) {
						const option = field.options.find(
							(o: any) => o.value === value || o.value?.toString() === value?.toString(),
						);
						displayValue = option?.label || value;
					}

					this.activeFilterChips.push({
						key,
						label: displayValue,
						value,
					});
				}
			}
		}
	}

	/**
	 * Handles filter chip removal.
	 * Removes filter from currentFilters, updates chips, and reloads data.
	 */
	onRemoveFilterChip(chip: { key: string; label: string; value: any }): void {
		delete this.currentFilters[chip.key];
		this.updateFilterChips();
		this.page = 1;
		this.loadData();
	}

	/**
	 * Opens the filter panel. Override to provide the filter component reference.
	 */
	onFilterClick(event: any, filterComponent?: FilterComponent): void {
		if (event.action === 'open' && event.button && filterComponent) {
			filterComponent.show(event.button);
		}
	}

	/**
	 * Handles filter changes from the filter component.
	 * Updates currentFilters, chips, and reloads data.
	 */
	onFilterChange(filters: any): void {
		this.currentFilters = filters;
		this.updateFilterChips();
		this.page = 1;
		this.loadData();
	}

	/** Select/date filters shown as pills in the table header; free-text filters go through the search box. */
	get pillFilters(): any[] {
		return this.filterFields.filter((f) => f.type === 'select' || f.type === 'datepicker');
	}

	get hasActiveFilters(): boolean {
		return Object.values(this.currentFilters ?? {}).some((v) => v !== null && v !== undefined && v !== '');
	}

	/**
	 * Handles a pill filter change: sets or clears that key and reloads from page 1.
	 */
	onInlineFilterChange(event: { key: string; value: any }): void {
		const next = { ...this.currentFilters };
		if (event.value === null || event.value === undefined || event.value === '') {
			delete next[event.key];
		} else {
			next[event.key] = event.value;
		}
		this.onFilterChange(next);
	}
}
